import type { ReactNode } from 'react';
import { translateIn, useLanguage } from '../contexts/LanguageContext';

// TEXT THAT TAKES THE SAME ROOM IN BOTH LANGUAGES.
//
// A table sizes its columns to what is in them, so a header that is "People" in English and
// "Personnes" in French moved every column each time the language was toggled. This draws the
// text in the language on screen and, in the same grid cell, the other language invisibly: the
// cell is as wide (and as tall) as the longer of the two, whichever is showing. No widths are
// written down anywhere, so a label that changes changes nothing to keep in step.
//
// For translated text only. A value read off a manual is the same in both languages and needs
// none of this.
export function StableText({ shown, other }: { shown: ReactNode; other: ReactNode }) {
  return (
    // CENTRED IN THE ROOM IT KEEPS: the other language can take two lines where this one takes
    // one, and the shorter is then drawn in the middle of the two, not on the first.
    <span className="inline-grid">
      <span className="self-center [grid-area:1/1]">{shown}</span>
      <span aria-hidden="true" className="invisible self-center [grid-area:1/1]">{other}</span>
    </span>
  );
}

// `st(key)` where a table would write `t(key)`. `fill` is for a label with placeholders —
// `st('notice.x', (s) => s.replace('{n}', '3'))` — applied to both languages alike.
export function useStableText() {
  const { language } = useLanguage();
  const other = language === 'en' ? 'fr' : 'en';
  return (key: string, fill: (s: string) => string = (s) => s): ReactNode => (
    <StableText shown={fill(translateIn(language, key))} other={fill(translateIn(other, key))} />
  );
}
